import {
	FormControlLabel,
	Switch,
} from "@mui/material";
import { ProfileForm_USER } from "../forms/form-components/user-profile.form";
import { useCentralStore } from "../store/Central";
import { useUserManagementStore } from "../store/UserManagement";
import { UserCog, Shield, ShieldAlert } from "lucide-react";
import { HeaderPortal } from "@/components/src/header-portal/header-portal.component";
import { PageTitle } from "@/components/src/page-title/page-title.component";
import { supabase } from "@/lib/supabase";
import { SupabaseWriteTableEnum } from "@/lib/supabase/supabaseTypes";
import { StorageBucketsEnum } from "../types/enums";
import { RolesEnum } from "@/lib/types/roles.types";
import { showNotification } from "../store/Central/selectors";
import { ImageInput } from "../forms/form-components/client/input-components/image-input";
import { Button as ShadcnButton } from "../components/ui/button";

export const AccountPage: React.FC = () => {
	const handleSignOut = useCentralStore((state) => state.handleSignOut);
	const { setMFADialog, mfaEnrolled, mfaVerified, role, organization } =
		useCentralStore((state) => ({
			setMFADialog: state.setMFADialog,
			mfaEnrolled: state.mfaEnrolled,
			mfaVerified: state.mfaVerified,
			role: state.role,
			organization: state.organization,
		}));
	const profile = useCentralStore((state) => state.profile);
	if (!mfaVerified) return null;

	const toggleAnalytics = async () => {
		if (!organization?.id) return;

		const { error } = await supabase
			.from(SupabaseWriteTableEnum.ORGANIZATIONS)
			.update({
				is_analytics: !organization?.is_analytics,
			})
			.eq("id", organization?.id);
		if (error) {
			showNotification({
				message:
					"Fehler beim Ändern der Analyse-Einstellungen. Sollte das Problem anhalten, melden Sie sich bitte bei unserem Support.",
				type: "error",
			});
		} else {
			// reload the page so the state is reflected globally and the script tag is updated accordingly
			window.location.reload();
		}
	};

	const handleFileChange = async (file: File | null) => {
		if (!file) return;
		const { uploadProfileAvatar } = useUserManagementStore.getState();
		await uploadProfileAvatar(file);
	};

	return (
		<div className="max-w-4xl mx-auto px-6 py-8">
			<HeaderPortal>
				<PageTitle title={[{ name: "Kontoeinstellungen" }]} icon={<UserCog />} />
			</HeaderPortal>
			
			<div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-4">
				{/* Profile Image & Security Section - Left Column */}
				<div className="md:col-span-1 space-y-6">
					<div className="bg-white rounded-lg shadow-sm border border-gray-100 p-6">
						<h2 className="text-lg font-medium text-gray-700 mb-4">Profilbild</h2>
						<div className="flex justify-center">
							<ImageInput
								bucket={StorageBucketsEnum.PUBLIC_IMAGES}
								organizationId={organization?.id ?? ""}
								avatarPath={profile?.avatar_url ?? ""}
								onFileChange={handleFileChange}
							/>
						</div>
					</div>
					
					<div className="bg-white rounded-lg shadow-sm border border-gray-100 p-6">
						<h2 className="text-lg font-medium text-gray-700 mb-4">Sicherheit</h2>
						<div className="flex flex-col gap-4">
							{!mfaEnrolled ? (
								<div className="flex flex-col gap-2">
									<div className="flex items-center gap-2 text-amber-600">
										<ShieldAlert size={18} />
										<span className="text-sm">Mehrfaktor-Authentifizierung nicht konfiguriert</span>
									</div>
									<ShadcnButton 
										variant="outline" 
										className="mt-2"
										onClick={() => setMFADialog(true)}
									>
										MFA einrichten
									</ShadcnButton>
								</div>
							) : (
								<div className="flex items-center gap-2 text-emerald-600">
									<Shield size={18} />
									<span className="text-sm">Mehrfaktor-Authentifizierung aktiv</span>
								</div>
							)}
							
							<div className="mt-4 pt-4 border-t border-gray-100">
								<ShadcnButton
									variant="outline"
									className="w-full text-rose-600 hover:text-rose-700 hover:bg-rose-50 border-rose-200"
									onClick={handleSignOut}
								>
									Abmelden
								</ShadcnButton>
							</div>
						</div>
					</div>
				</div>
				
				{/* Profile Form - Right Column */}
				<div className="md:col-span-2">
					<div className="bg-white rounded-lg shadow-sm border border-gray-100 p-6">
						<h2 className="text-lg font-medium text-gray-700 mb-4">Persönliche Daten</h2>
						<ProfileForm_USER />
						
						{role === RolesEnum.OWNER && (
							<div className="mt-8 pt-6 border-t border-gray-100">
								<h3 className="text-sm font-medium text-gray-700 mb-3">Einstellungen</h3>
								<FormControlLabel
									control={
										<Switch
											checked={organization?.is_analytics ?? false}
											onChange={toggleAnalytics}
											size="small"
										/>
									}
									sx={{
										"& .MuiFormControlLabel-label": {
											fontSize: "0.875rem",
											color: "rgb(55, 65, 81)",
										},
									}}
									label="Erlauben Sie das Sammeln anonymisierter Daten zur Verbesserung von dentLAB"
								/>
							</div>
						)}
					</div>
				</div>
			</div>
		</div>
	);
};